import { Component, Event, EventEmitter, Fragment, h, Method, Prop, State } from '@stencil/core';
import { RadioInputAttr, VsRadioData } from './vs-radio.model';
import { VALIDATION_ATTRIBUTES } from '../../vs-form-builder.model';
import { checkValidations, getValidStateAccordingToRequiredStatus } from '../../helpers/validator.helper';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';

@Component({
  tag: 'vs-radio',
  shadow: false,
})

export class VsRadio {

  /**
   * @shortDescription Identifier of the input field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription Index of radio field in a form.
   * @controlName index */
  @Prop() index: number;

  /**
   * @shortDescription The label for the radio group
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription The data to render for the radio group
   * @controlName data */
  @Prop() data!: VsRadioData[];

  /**
   * @shortDescription Validators rules to check against for the radio.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   * @shortDescription Label exists in the radio field as eyebrow view.
   * @controlName eyebrow */
  @Prop() eyebrow: boolean = false;

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   * @shortDescription Keeps state of the currently selected value
   * @controlName value */
  @State() value: string;

  /**
   * @shortDescription Enable inline view of radio button
   * @controlName isInline */
  @Prop() isInline: boolean;

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted before the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;

  /* END GENAI@GHCOPILOT */

  /**
   * @shortDescription Event to inform parent when the value changes
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Emit Input blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  @State() isRequired: boolean;
  @State() inputAllyAlert: string = 'radio_alert_';
  @State() showError: boolean = false;
  @State() valid: boolean = false;
  @State() selectedErrorMessage: string = '';

  /**
   * @shortDescription reset value of radio
   * @controlName setFocus */
  @Method()
  async resetValue(isShowError = false) {
    this.value = '';
    this.handleRadioOnChange(this.value);
    this.showError = isShowError;
  }

  /**
   * @shortDescription used by parent to indicate validation error
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.validate(this.value, true);
  }

  private validate = (value: string, showError: boolean) => {
    const validationAttr = checkValidations(this.validators, value);
    this.selectedErrorMessage = validationAttr.errorMessage;
    this.valid = getValidStateAccordingToRequiredStatus(this.isRequired, value, validationAttr);
    if (showError) {
      this.showError = !this.valid;
    }
  };

  private onBlurInput = () => {
    this.showError = true;
    this.blurEvent.emit();
  };

  componentWillLoad() {
    this.isRequired = this.validators.findIndex(validator => validator.key === VALIDATION_ATTRIBUTES.REQUIRE) > -1;
    this.validate(this.value, false);
    // initialise value to form model
    this.computedInputValue.emit({
        index: this.index,
        key: this.inputId,
        value: '',
        isValid: this.valid,
        error: this.selectedErrorMessage,
      } as RadioInputAttr,
    );
  }

  /**
   * @shortDescription Method to update state when selected radio value changes
   * @controlName handleRadioOnChange */
  private handleRadioOnChange = (evt) => {
    this.value = evt.target.value;
    this.validate(this.value, true);
    this.computedInputValue.emit({
        index: this.index,
        key: this.inputId,
        value: this.value,
        isValid: this.valid,
        error: this.selectedErrorMessage,
      } as RadioInputAttr,
    );
  };

  private generateAriaLabel = () => {
    return (this.showError && !this.valid && this.selectedErrorMessage) ? this.inputAllyAlert + this.inputId : undefined;
  };

  render() {
    return (
      <Fragment>
        {this.aboveInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.aboveInputContent} />}
        <fieldset id={this.inputId} class='vs-fieldset'>
          {this.label && (
            <legend class={`vs-label vs-has-focus`} aria-describedby={this.generateAriaLabel()}>
              <span innerHTML={this.label} />
              {this.isRequired ? <strong class='required'><i> {this.requiredText} </i></strong> : ''}
            </legend>
          )}
          {this.betweenInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.betweenInputContent} />}
          <div class={this.isInline ? 'vs-d-flex vs-form-group vs-mx-0 vs-mb-0' : 'vs-form-group vs-mb-0'}>
            {this.data && (
              this.data.map((el, idx) => {
                return (
                  <div class='vs-mb-0 vs-d-flex vs-flex-grow-1'>
                    <input
                      key={idx}
                      id={`${this.inputId}-${idx}`}
                      name={this.inputId}
                      class={'vs-input-radio vs-mb-1 ' + (this.showError && !this.valid ? 'has-error' : '')}
                      type='radio'
                      value={idx}
                      checked={idx.toString() === this.value}
                      disabled={el.disabled}
                      onChange={this.handleRadioOnChange}
                      onBlur={this.onBlurInput}
                      aria-describedby={this.generateAriaLabel()}
                      aria-invalid={this.showError && !this.valid}
                      aria-checked={idx.toString() === this.value}
                    />
                    <label class='vs-text' htmlFor={`${this.inputId}-${idx}`}>
                      <span innerHTML={el.value} />
                    </label>
                  </div>
                );
              })
            )
            }</div>
          {this.showError && !this.valid && this.selectedErrorMessage && (
            <p role='alert' aria-live='polite' class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
               id={this.inputAllyAlert + this.inputId}>
              <span aria-hidden='true' class='visa-icon-alert' />
              <span innerHTML={decodeHTMLEntities(this.selectedErrorMessage)} />
            </p>
          )}
        </fieldset>
        {this.belowInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.belowInputContent} />}
      </Fragment>
    );
  }

}
